import { useEffect, useMemo, useRef, useState } from "react";
import RecurrenceCustomEditor from "@/components/RecurrenceCustomEditor";
import { PRESETS, isPresetRule } from "@/lib/recurrence";

interface Props {
  value: string | null;
  onChange: (next: string | null) => void;
}

function labelFor(rule: string | null): string {
  if (!rule) return "Повторение";
  const match = PRESETS.find((p) => p.rule === rule);
  return match ? match.label : "Настроить";
}

export default function RecurrencePicker({ value, onChange }: Props) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<"presets" | "custom">("presets");
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) {
      setMode("presets");
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function handle(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", handle);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", handle);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  function pickPreset(next: string | null) {
    onChange(next);
    setOpen(false);
  }

  const isCustomActive = useMemo(() => Boolean(value && !isPresetRule(value)), [value]);

  return (
    <div className="relative" ref={wrapRef}>
      <button type="button" onClick={() => setOpen((v) => !v)} className="details-field">
        <svg
          className={`w-4 h-4 flex-shrink-0 ${value ? "text-blue-500" : "text-gray-400"}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
        </svg>
        <span>{labelFor(value)}</span>
        {value && (
          <span
            role="button"
            tabIndex={0}
            aria-label="Очистить повторение"
            onClick={(e) => {
              e.stopPropagation();
              onChange(null);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                e.stopPropagation();
                onChange(null);
              }
            }}
            className="details-field-clear ml-auto"
          >
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </span>
        )}
      </button>
      {open && mode === "presets" && (
        <div className="details-popover" data-testid="recurrence-popover">
          {PRESETS.map((p) => (
            <button
              key={p.rule}
              type="button"
              onClick={() => pickPreset(p.rule)}
              className="details-popover-item"
            >
              <span className="flex-1 text-left">{p.label}</span>
              {p.rule === value && (
                <svg className="w-3 h-3 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              )}
            </button>
          ))}
          <button
            type="button"
            onClick={() => setMode("custom")}
            className="details-popover-item"
          >
            <span className="flex-1 text-left">Настроить…</span>
            {isCustomActive && (
              <svg className="w-3 h-3 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
              </svg>
            )}
          </button>
          {value && (
            <button
              type="button"
              onClick={() => pickPreset(null)}
              className="details-popover-item text-red-600 dark:text-red-400"
            >
              Убрать
            </button>
          )}
        </div>
      )}

      {open && mode === "custom" && (
        <div className="details-popover" data-testid="recurrence-custom">
          <RecurrenceCustomEditor
            value={value}
            onSave={(rule) => {
              onChange(rule);
              setOpen(false);
            }}
            onBack={() => setMode("presets")}
            onCancel={() => setOpen(false)}
          />
        </div>
      )}
    </div>
  );
}
